import type { Capability } from '~/constants/huiguanjia-home'

import type { InjectionKey } from 'vue'

import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  provide,
  reactive,
  readonly,
  shallowRef,
  watch,
} from 'vue'

import { applyPartner } from '~/api'
import { capabilities } from '~/constants/huiguanjia-home'

type FormMessageType = '' | 'error' | 'success'

export const useHuiguanjiaHome = () => {
  const defaultCapability = capabilities[0]
  if (!defaultCapability) {
    throw new Error('Capabilities configuration cannot be empty.')
  }

  const activeCapability = shallowRef(defaultCapability.key)
  const activeSection = shallowRef('hero')
  const isMenuOpen = shallowRef(false)
  const isScrolled = shallowRef(false)
  const contactsError = shallowRef(false)
  const phoneError = shallowRef(false)
  const companyError = shallowRef(false)
  const formMessage = shallowRef('')
  const formMessageType = shallowRef<FormMessageType>('')
  const isSubmitting = shallowRef(false)
  const form = reactive({
    contacts: '',
    phone: '',
    company: '',
    message: '',
  })

  const currentCapability = computed<Capability>(
    () => capabilities.find((item) => item.key === activeCapability.value) ?? defaultCapability
  )

  let sectionObserver: IntersectionObserver | undefined
  let releaseSectionLockTimer: number | undefined
  let isSectionLockActive = false

  const setActiveCapability = (key: string) => {
    activeCapability.value = key
  }

  const focusCapabilityTab = (index: number) => {
    const item = capabilities[index]
    if (!item) return

    activeCapability.value = item.key
    nextTick(() => {
      document.querySelector<HTMLElement>(`#${CSS.escape(`capability-tab-${item.key}`)}`)?.focus()
    })
  }

  const handleCapabilityKeydown = (event: KeyboardEvent, index: number) => {
    if (!['ArrowLeft', 'ArrowRight', 'End', 'Home'].includes(event.key)) return

    event.preventDefault()
    const lastIndex = capabilities.length - 1
    let nextIndex = index

    switch (event.key) {
      case 'ArrowLeft': {
        nextIndex = index === 0 ? lastIndex : index - 1
        break
      }
      case 'ArrowRight': {
        nextIndex = index === lastIndex ? 0 : index + 1
        break
      }
      case 'End': {
        nextIndex = lastIndex
        break
      }
      case 'Home': {
        nextIndex = 0
        break
      }
    }

    focusCapabilityTab(nextIndex)
  }

  const releaseSectionLock = () => {
    isSectionLockActive = false
    if (releaseSectionLockTimer) {
      window.clearTimeout(releaseSectionLockTimer)
      releaseSectionLockTimer = undefined
    }
    window.removeEventListener('scrollend', releaseSectionLock)
  }

  const lockSectionHighlight = () => {
    releaseSectionLock()
    isSectionLockActive = true

    if ('onscrollend' in window) {
      window.addEventListener('scrollend', releaseSectionLock, { once: true })
    }

    releaseSectionLockTimer = window.setTimeout(releaseSectionLock, 1200)
  }

  const scrollToSection = (sectionId: string) => {
    activeSection.value = sectionId
    isMenuOpen.value = false

    if (typeof document === 'undefined') return

    const target = document.querySelector<HTMLElement>(`#${CSS.escape(sectionId)}`)
    if (!target) return

    lockSectionHighlight()
    target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    window.history.pushState(null, '', `#${sectionId}`)
  }

  const updateScrollState = () => {
    isScrolled.value = window.scrollY > 12
  }

  const clearContactsError = () => {
    contactsError.value = false
  }

  const clearPhoneError = () => {
    phoneError.value = false
  }

  const clearCompanyError = () => {
    companyError.value = false
  }

  const handleSubmit = async () => {
    if (isSubmitting.value) return

    const contacts = form.contacts.trim()
    const phone = form.phone.trim()
    const company = form.company.trim()
    const remark = form.message.trim() || '无'
    const phoneValid = /^1[3-9]\d{9}$/.test(phone)

    contactsError.value = !contacts
    phoneError.value = !phoneValid
    companyError.value = !company

    if (!contacts) {
      formMessageType.value = 'error'
      formMessage.value = '请填写您的姓名，方便顾问与您沟通。'
      return
    }

    if (!phoneValid) {
      formMessageType.value = 'error'
      formMessage.value = '请填写正确的手机号，或直接拨打 400-600-7709 咨询。'
      return
    }

    if (!company) {
      formMessageType.value = 'error'
      formMessage.value = '请填写公司或社区名称，方便顾问判断适合的方案。'
      return
    }

    isSubmitting.value = true
    formMessageType.value = ''
    formMessage.value = '正在提交留言...'

    try {
      await applyPartner({
        company,
        contacts,
        phone,
        remark,
      })
      formMessageType.value = 'success'
      formMessage.value = '留言已提交，我们会尽快与您联系。'
      form.contacts = ''
      form.phone = ''
      form.company = ''
      form.message = ''
    } catch {
      formMessageType.value = 'error'
      formMessage.value = '提交失败，请稍后重试，或直接拨打 400-600-7709 咨询。'
    } finally {
      isSubmitting.value = false
    }
  }

  onMounted(() => {
    updateScrollState()
    window.addEventListener('scroll', updateScrollState, { passive: true })
    window.addEventListener('resize', updateScrollState, { passive: true })

    const sections = document.querySelectorAll<HTMLElement>('[data-section]')
    sectionObserver = new IntersectionObserver(
      (entries) => {
        if (isSectionLockActive) return

        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            activeSection.value = (entry.target as HTMLElement).id
          }
        })
      },
      { rootMargin: '-35% 0px -55% 0px', threshold: 0 }
    )

    sections.forEach((section) => sectionObserver?.observe(section))

    if (window.location.hash) {
      const sectionId = window.location.hash.slice(1)
      if (sectionId) {
        window.requestAnimationFrame(() => scrollToSection(sectionId))
      }
    }
  })

  watch(isMenuOpen, (value) => {
    if (typeof document === 'undefined') return
    document.body.classList.toggle('overflow-hidden', value)
  })

  onBeforeUnmount(() => {
    sectionObserver?.disconnect()
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', updateScrollState)
      window.removeEventListener('resize', updateScrollState)
      releaseSectionLock()
    }
    if (typeof document !== 'undefined') {
      document.body.classList.remove('overflow-hidden')
    }
  })

  return {
    activeCapability: readonly(activeCapability),
    activeSection: readonly(activeSection),
    clearCompanyError,
    clearContactsError,
    clearPhoneError,
    companyError: readonly(companyError),
    contactsError: readonly(contactsError),
    currentCapability,
    form,
    formMessage: readonly(formMessage),
    formMessageType: readonly(formMessageType),
    handleCapabilityKeydown,
    handleSubmit,
    isMenuOpen,
    isScrolled: readonly(isScrolled),
    isSubmitting: readonly(isSubmitting),
    phoneError: readonly(phoneError),
    scrollToSection,
    setActiveCapability,
  }
}

export type HuiguanjiaHomeContext = ReturnType<typeof useHuiguanjiaHome>

const huiguanjiaHomeKey: InjectionKey<HuiguanjiaHomeContext> = Symbol('huiguanjia-home')

export const provideHuiguanjiaHome = () => {
  const context = useHuiguanjiaHome()
  provide(huiguanjiaHomeKey, context)

  return context
}

export const useHuiguanjiaHomeContext = () => {
  const context = inject(huiguanjiaHomeKey)

  if (!context) {
    throw new Error('Huiguanjia home context is not provided.')
  }

  return context
}
